import { ReactNode, useEffect, useRef, useState } from 'react'
import { Button } from './button'
import { CloudinaryImage } from './cloudinary-image'
import { LOADING } from './loading'

type FileInputProps = {
	className?: string
	name: string
	value?: string
	label?: ReactNode
	width?: number
	height?: number
	multiple?: boolean
}

const UW_CONFIG = {
	cloudName: process.env.CLOUDINARY_CLOUD_NAME,
	uploadPreset: process.env.CLOUDINARY_UPLOAD_PRESET,
}

export function CloudinaryInput({
	label,
	name,
	className,
	value,
	width,
	height,
	multiple,
}: FileInputProps) {
	const [publicIds, setPublicIds] = useState<Array<string>>(value ? [value] : [])

	return (
		<div className={`flex flex-col gap-1 ${className ?? ''}`}>
			{label && <div className="flex gap-1 font-normal leading-tight text-xs">{label}</div>}
			<CloudinaryUploadWidget setPublicIds={setPublicIds} multiple={multiple} />
			{multiple ? <input type="hidden" name="__many" value={name} /> : null}
			{publicIds.map(publicId => (
				<input key={publicId} type="hidden" name={`${name}[]`} value={publicId} />
			))}
			<div className="flex flex-wrap gap-4">
				{publicIds.map(publicId => (
					<CloudinaryImage key={publicId} id={publicId} width={width} height={height} />
				))}
			</div>
		</div>
	)
}

const CloudinaryUploadWidget = ({
	setPublicIds,
	multiple = false,
}: {
	setPublicIds: (publicIds: Array<string>) => void
	multiple?: boolean
}) => {
	const uploadWidgetRef = useRef<ReturnType<typeof window.cloudinary.createUploadWidget> | null>(
		null,
	)
	const [scriptStatus, setScriptStatus] = useState<'idle' | 'loading' | 'loaded' | 'error'>(
		'idle',
	)

	useEffect(() => {
		if (scriptStatus !== 'idle') return
		if (window.cloudinary) {
			setScriptStatus('loaded')
			return
		}
		setScriptStatus('loading')
		const script = document.createElement('script')
		script.src = 'https://upload-widget.cloudinary.com/global/all.js'
		script.onload = () => setScriptStatus('loaded')
		script.onerror = () => setScriptStatus('error')
		document.head.appendChild(script)
	}, [scriptStatus])

	const onClick = () => {
		if (uploadWidgetRef.current) {
			uploadWidgetRef.current.open()
		}
	}

	useEffect(() => {
		if (scriptStatus !== 'loaded') return

		if (!window.cloudinary) {
			setScriptStatus('error')
			return
		}

		uploadWidgetRef.current = window.cloudinary.createUploadWidget(
			{ ...UW_CONFIG, multiple },
			(error, result) => {
				console.log(error, result)
				if (!error && result && result.event === 'queues-end') {
					setPublicIds(result.info.files.map((file: any) => file.uploadInfo.public_id))
				}
			},
		)
	}, [scriptStatus, setPublicIds])

	return (
		<>
			{scriptStatus === 'loading' ? LOADING : <Button onPress={onClick}>Upload</Button>}
			{scriptStatus === 'error' && <div>Error loading Cloudinary widget</div>}
		</>
	)
}
